// Time copy for operation-bar pills (opbar 1a).
// Counted jobs: time left from this run's own pace. Uncounted (batch) jobs:
// elapsed time plus the usual range from past runs (GET /run-estimate).

/** One observation of a counted job's progress. `t` in ms. */
export interface ProgressSample {
  t: number;
  done: number;
}

// Don't guess from a handful of items or a few seconds: early pace is noisy
// (warm-up, the first S2 lookups) and a wild first number erodes trust.
const MIN_ITEMS = 3;
const MIN_MS = 5_000;

/** Seconds left at the pace observed since `first`, or null when too early to say.
 *  Pace is measured from the first sample this mount saw — not from job start —
 *  so a remount mid-run (timer reset) can't divide a large count by a tiny time. */
export function secondsLeft(first: ProgressSample, now: ProgressSample, total: number): number | null {
  const items = now.done - first.done;
  const ms = now.t - first.t;
  if (items < MIN_ITEMS || ms < MIN_MS || total <= now.done) return null;
  return ((total - now.done) * ms) / items / 1000;
}

export function formatTimeLeft(seconds: number): string {
  if (seconds < 60) return "<1 min left";
  return `~${Math.round(seconds / 60)} min left`;
}

function fmt(seconds: number): string {
  if (seconds < 90) return `${Math.round(seconds)}s`;
  if (seconds < 90 * 60) return `${Math.round(seconds / 60)} min`;
  return `${Math.round(seconds / 3600)} h`;
}

/** "usually 4–15 min" from a measured p25–p75 range. */
export function formatUsual(lowSec: number, highSec: number): string {
  const lo = fmt(lowSec);
  const hi = fmt(highSec);
  if (lo === hi) return `usually ~${lo}`;
  // Drop the repeated unit: "4–15 min", not "4 min–15 min".
  const [loV, loU] = lo.split(/(?<=\d)(?=\D)/);
  const [hiV, hiU] = hi.split(/(?<=\d)(?=\D)/);
  return loU === hiU ? `usually ${loV}–${hiV}${hiU}` : `usually ${lo}–${hi}`;
}

/** Stores mark a user cancel by failing with a "cancel" message; that is not an error. */
export function isCancelMessage(msg: string | null | undefined): boolean {
  return !!msg && /cancel/i.test(msg);
}
